// Trident results (source_type 'trident', from the View 7 or the round
// display) carry no test time: the Apex reports none, and the server stamps
// each row with the time it arrived (docs/proposals/2026-10-06-trident-
// test-time.md). A Trident that has stopped testing keeps handing on its
// last numbers looking new. Owner, 2026-10-06: the Trident path is still
// not working, so its readings are never marked fresh. They are shown with
// "test time unknown", and never graded, alerted on, used for a test task
// or dosed from, until the displays send when each test finished. Pure,
// shared by the server and /app.

import { INSTRUMENT_MARGIN } from './instrument-margin.js';

export const TRIDENT_TEST_TIME_KNOWN = false;

// What a Trident (or Trident NP) tests itself. The round display sends the
// Apex tank probes (temperature, pH, ORP, salinity) in the same 'trident'
// source; those report live and keep their usual checks, so a heater stuck
// on still alerts.
export const TRIDENT_TESTED = new Set(['alk', 'calcium', 'magnesium', 'nitrate', 'phosphate']);

export function testTimeUnknown(row, param = row?.param) {
  return !TRIDENT_TEST_TIME_KNOWN && row?.source_type === 'trident' && TRIDENT_TESTED.has(param);
}

// A Trident NP result at or below the kit's margin reads "none". On a
// low-nutrient reef every test says so for days, which is the tank, not a
// stopped tester, so it is not called frozen. The same Trident's alkalinity
// still catches a stop.
const READS_NONE = new Set(['nitrate', 'phosphate']);
export function tridentReadsNone(row, param = row?.param) {
  if (!testTimeUnknown(row, param) || !READS_NONE.has(param)) return false;
  const v = Number(row?.value);
  return Number.isFinite(v) && v <= INSTRUMENT_MARGIN[param];
}

// The rows that speak for the tank now, newest first. The Trident writes a
// row every few minutes, so without this the next one buries the keeper's
// own test. The keeper's own last test outranks Trident rows for as long as
// any keeper's test of that param counts (`isRecent`: the param's usual
// horizon, 21 days for alkalinity), exactly as it would with no Trident:
// nothing flips on a timer, and an off-band test keeps alerting. Trident
// numbers are then left out of the series entirely (they never confirm or
// contradict a test of known time); when one disagrees with the test,
// outrankedTrident says so on Home and the dose calculator asks first, so
// a reactor running dry is not hidden. With no counting test of their own
// the Trident's rows stay, shown with their caveat. A list with no
// Trident-tested Trident row is returned as is.
export function currentSeries(list, param, isRecent) {
  if (!Array.isArray(list) || !list.length) return list;
  const own = list.filter((r) => !testTimeUnknown(r, param));
  if (own.length === list.length || !own.length) return list;
  if (!testTimeUnknown(list[0], param)) return own;
  return isRecent(own[0]) ? own : list;
}

// The Trident's newest number when a keeper's own test outranks it and the
// two disagree beyond kit error: shown to the keeper, never graded. Null
// otherwise. `agree(param, a, b)` is shared/instrument-margin.js
// readingsAgree, passed in so this module stays dependency-free.
export function outrankedTrident(list, param, agree, isRecent) {
  if (!Array.isArray(list) || !list.length || !testTimeUnknown(list[0], param)) return null;
  const cur = currentSeries(list, param, isRecent);
  if (cur === list || !cur.length) return null;
  return agree(param, list[0].value, cur[0].value) ? null : { trident: list[0], own: cur[0] };
}
